import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useApp } from "../AppContext";
import api from "../api";

const Testimonials = () => {
    const { t, lang, apiBase } = useApp();
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get(`${apiBase}/testimonials`)
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    }, [apiBase]);

    if (items.length === 0) return null;

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="relative py-24 sm:py-32 bg-earth-100 grain overflow-hidden"
        >
            {/* Dominican flag accent stripes */}
            <div className="absolute top-0 left-0 right-0 h-1 flex">
                <div className="flex-1" style={{ background: "#002D62" }} />
                <div className="flex-1 bg-white" />
                <div className="flex-1" style={{ background: "#CE1126" }} />
            </div>

            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <span className="pill-tag mb-5" data-testid="testimonials-tag">
                        {lang === "es" ? "Testimonios" : "Testimonials"}
                    </span>
                    <h2 className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {lang === "es"
                            ? "Lo que dicen nuestras familias"
                            : "What our families say"}
                    </h2>
                    <p className="mt-5 text-lg text-earth-700">
                        {lang === "es"
                            ? "Sabor que une corazones, de Santo Domingo a Montego Bay."
                            : "Flavors that unite hearts, from Santo Domingo to Montego Bay."}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {items.map((it, i) => (
                        <motion.figure
                            key={it.id}
                            data-testid={`testimonial-${i}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-30px" }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="relative bg-white rounded-3xl p-8 shadow-sm border border-earth-200 flex flex-col"
                        >
                            <Quote className="w-10 h-10 text-terracotta-200 -mb-2" />
                            <div className="flex gap-0.5 mb-3">
                                {Array.from({ length: it.rating || 5 }).map((_, k) => (
                                    <Star
                                        key={k}
                                        className="w-4 h-4 fill-terracotta-500 text-terracotta-500"
                                    />
                                ))}
                            </div>
                            <blockquote className="text-earth-800 leading-relaxed flex-1 italic font-serif text-lg">
                                "
                                {lang === "es"
                                    ? it.text_es
                                    : it.text_en || it.text_es}
                                "
                            </blockquote>
                            <figcaption className="mt-5 pt-5 border-t border-earth-100 flex items-center gap-3">
                                <div
                                    className="w-11 h-11 rounded-full flex items-center justify-center text-white font-serif font-bold text-base shrink-0"
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #C85A17 0%, #CE1126 100%)",
                                    }}
                                >
                                    {it.author.charAt(0)}
                                </div>
                                <div className="leading-tight">
                                    <div className="font-bold text-earth-900 text-sm">
                                        {it.author}
                                    </div>
                                    {it.location && (
                                        <div className="text-xs text-earth-500">
                                            {it.location}
                                        </div>
                                    )}
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
